import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebaseconfig';
import { fetchCategories } from '../utils/categories';
import { 
  FaQuestion,
  FaUtensils,
  FaGraduationCap,
  FaShoppingBasket,
  FaLightbulb,
  FaTint,
  FaUsers,
  FaCoffee,
  FaWineGlass,
  FaWater,
  FaToolbox,
  FaHandHoldingWater,
  FaHandHoldingHeart
} from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { USER_ROLES } from '../utils/roles';
import './Expenses.css';

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [timeRange, setTimeRange] = useState('today');
  const [, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const { currentUser, userRole } = useAuth();
  const isAdmin = userRole === USER_ROLES.ADMIN;

  useEffect(() => {
    if (userRole === USER_ROLES.STAFF) {
      navigate('/staff-savings');
    }
    const loadData = async () => {
      try {
        const [fetchedCategories, stockItemsSnapshot] = await Promise.all([
          fetchCategories(),
          getDocs(query(collection(db, 'stock_items'), orderBy('name', 'asc')))
        ]);
        setCategories(fetchedCategories);
        setStockItems(stockItemsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })));
      } catch (e) {
        console.error('Error loading data:', e);
        setError('Failed to load data');
      }
    };
    loadData();
  }, [userRole, navigate]);

  useEffect(() => {
    const fetchExpenses = async () => {
      if (!currentUser) {
        console.log("No user data yet, waiting...");
        return;
      }

      try {
        setLoading(true);
        const expensesRef = collection(db, 'expenses');
        let q = query(expensesRef, orderBy('createdAt', 'desc'));

        // Filter by category if selected
        if (selectedCategory !== 'all') {
          q = query(q, where('category', '==', selectedCategory));
        }

        // Only filter by user if not an admin
        console.log("isAdmin: " + isAdmin);
        if (!isAdmin) {
          q = query(q, where('userId', '==', currentUser.uid));
        }

        const querySnapshot = await getDocs(q);
        const expensesList = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            date: data.date?.toDate?.() || new Date(data.date),
            createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt)
          };
        });

        setExpenses(expensesList);
        setError('');
      } catch (err) {
        console.error('Error fetching expenses:', err);
        setError('Failed to load expenses');
      } finally {
        setLoading(false);
      }
    };

    fetchExpenses();
  }, [selectedCategory, currentUser, isAdmin]);

  const handleDelete = async (expenseId) => {
    if (!isAdmin) {
      setError('Only administrators can delete expenses');
      return;
    }

    if (window.confirm('Are you sure you want to delete this expense?')) {
      try {
        await deleteDoc(doc(db, 'expenses', expenseId));
        setExpenses(expenses.filter(expense => expense.id !== expenseId));
        setSuccess('Expense deleted successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        console.error('Error deleting expense:', err);
        setError('Failed to delete expense');
      }
    }
  };

  const formatDate = (date) => {
    if (!date) return 'No date';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount);
  };

  const getCategoryDetails = (categoryId) => {
    const found = categories.find(c => c.id === categoryId);
    return found || { id: categoryId, name: 'Other', color: '#95A5A6', icon: 'FaQuestion' };
  };

  const availableIcons = [
    { name: 'FaQuestion', icon: <FaQuestion /> },
    { name: 'FaUtensils', icon: <FaUtensils /> },
    { name: 'FaGraduationCap', icon: <FaGraduationCap /> },
    { name: 'FaShoppingBasket', icon: <FaShoppingBasket /> },
    { name: 'FaLightbulb', icon: <FaLightbulb /> },
    { name: 'FaTint', icon: <FaTint /> },
    { name: 'FaUsers', icon: <FaUsers /> },
    { name: 'FaCoffee', icon: <FaCoffee /> },
    { name: 'FaWineGlass', icon: <FaWineGlass /> },
    { name: 'FaWater', icon: <FaWater /> },
    { name: 'FaToolbox', icon: <FaToolbox /> },
    { name: 'FaHandHoldingWater', icon: <FaHandHoldingWater /> },
    { name: 'FaHandHoldingHeart', icon: <FaHandHoldingHeart /> }
  ];

  const getIconComponent = (iconName) => {
    const icon = availableIcons.find(i => i.name === iconName);
    return icon ? icon.icon : <FaQuestion />;
  };

  const getFilteredExpenses = () => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    
    return expenses.filter(expense => {
      if (!expense.date) return false;
      
      //const expenseDate = new Date(expense.date);
      /*const expenseDate = new Date(
        (expense.date?.toDate?.() || expense.createdAt?.toDate?.()) ?? null
      );*/
      const expenseDate = new Date(expense.createdAt);
      
      // Filter by time range
      let timeRangeMatch = true;
      switch (timeRange) {
        case 'yesterday':
          const startOfYesterday = new Date(now);
          startOfYesterday.setDate(now.getDate() - 1);
          startOfYesterday.setHours(0, 0, 0, 0);
          const endOfYesterday = new Date(now);
          endOfYesterday.setDate(now.getDate() - 1);
          endOfYesterday.setHours(23, 59, 59, 999);
          timeRangeMatch = expenseDate >= startOfYesterday && expenseDate <= endOfYesterday;
          break;
        case 'today':
          timeRangeMatch = expenseDate >= startOfToday && expenseDate <= endOfToday;
          break;
        case 'week':
          const weekAgo = new Date(now);
          weekAgo.setDate(now.getDate() - 7);
          timeRangeMatch = expenseDate >= weekAgo;
          break;
        case 'month':
          const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
          timeRangeMatch = expenseDate >= startOfMonth;
          break;
        case 'lastMonth':
          const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
          timeRangeMatch = expenseDate >= startOfLastMonth && expenseDate <= endOfLastMonth;
          break;
        case 'year':
          const startOfYear = new Date(now.getFullYear(), 0, 1);
          timeRangeMatch = expenseDate >= startOfYear;
          break;
        case 'lastYear':
          const startOfLastYear = new Date(now.getFullYear() - 1, 0, 1);
          const endOfLastYear = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999);
          timeRangeMatch = expenseDate >= startOfLastYear && expenseDate <= endOfLastYear;
          break;
        default:
          timeRangeMatch = true;
      }

      return timeRangeMatch;
    });
  };

  const calculateTotalAmount = (expensesList) => {
    return expensesList.reduce((total, expense) => total + (expense.amount || 0), 0);
  };

  const getTimeRangeLabel = () => {
    switch (timeRange) {
      case 'yesterday':
        return 'Yesterday';
      case 'today':
        return 'Today';
      case 'week':
        return 'This Week';
      case 'month':
        return 'This Month';
      case 'lastMonth':
        return 'Last Month';
      case 'year':
        return 'This Year';
      case 'lastYear':
        return 'Last Year';
      default:
        return 'All Time';
    }
  };

  if (!currentUser) {
    return <div className="loading">Loading user data...</div>;
  }

  const filteredExpenses = getFilteredExpenses();

  return (
    <div className="expenses-container">
      <div className="expenses-header">
        <h2>{isAdmin ? 'All Expenses' : 'My Expenses'}</h2>
        <div className="header-actions">
          <div className="filters">
            <div className="category-filter">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="category-select"
              >
                <option value="all">All Items</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="time-range-filter">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="time-range-select"
              >
                <option value="all">All Time</option>
                <option value="yesterday">Yesterday</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="lastMonth">Last Month</option>
                <option value="year">This Year</option>
                <option value="lastYear">Last Year</option>
              </select>
            </div>
          </div>
          <button 
            className="add-expense-button"
            onClick={() => navigate('/expenses/create')}
          >
            Add Expense
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="expenses-summary">
        <div className="total-amount">
          <h3>Total Amount ({getTimeRangeLabel()})</h3>
          <p>{formatAmount(calculateTotalAmount(filteredExpenses))}</p>
        </div>
      </div>

      <div className="expenses-list">
        {filteredExpenses.length === 0 ? (
          <div className="no-expenses">
            No expenses found for {selectedCategory === 'all' ? 'any item' : getCategoryDetails(selectedCategory).name} in {getTimeRangeLabel().toLowerCase()}
            {selectedCategory !== 'all' && (
              <button 
                className="clear-filter-button"
                onClick={() => setSelectedCategory('all')}
              >
                Clear Filter
              </button>
            )}
          </div>
        ) : (
          filteredExpenses.map(expense => {
            const categoryDetails = getCategoryDetails(expense.category);
            const isOwnExpense = expense.userId === currentUser.uid;
            
            return (
              <div key={expense.id} className="expense-card">
                <div className="expense-icon" style={{ backgroundColor: categoryDetails.color }}>
                  {getIconComponent(categoryDetails.icon)}
                </div>
                <div className="expense-details">
                  <h3>{expense.title || 'No title'}</h3>
                  <div className="expense-meta">
                    <p className="expense-category">{categoryDetails.name}</p>
                    {expense.expenseType && expense.expenseType !== 'regular' && (
                      <span className={`expense-type-badge ${expense.expenseType === 'stock_in' ? 'stock-in' : 'stock-out'}`}>
                        {expense.expenseType === 'stock_in' ? 'Stock In' : 'Stock Out'}
                      </span>
                    )}
                  </div>
                  {expense.stockItemId && (() => {
                    const linkedStockItem = stockItems.find(item => item.id === expense.stockItemId);
                    return (
                      <div className="linked-stock-info">
                        <strong>Stock Item:</strong> {linkedStockItem?.name || 'Unknown Item'}
                      </div>
                    );
                  })()}
                  {expense.description && (
                    <p className="expense-description">{expense.description}</p>
                  )}
                  <p className="expense-date">{formatDate(expense.date)}</p>
                  {isAdmin && !isOwnExpense && (
                    <p className="expense-owner">Created by: {expense.userEmail || 'Unknown User'}</p>
                  )}
                </div>
                <div className="expense-amount">
                  {formatAmount(expense.amount)}
                </div>
                <div className="expense-actions">
                  {isAdmin && (
                    <button
                      className="delete-button"
                      onClick={() => handleDelete(expense.id)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Expenses;
